import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';

export interface ListPageHeaderProps {
  /** Page title, rendered as an h4. Must be a translated string. */
  title: string;
  /** Optional subtitle rendered below the title. Must be a translated string. */
  subtitle?: string;
  /**
   * Right-aligned actions (primary and/or secondary buttons). Composed by the
   * caller so this component stays domain-agnostic.
   */
  actions?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Shared list-page header: title, optional subtitle and right-aligned actions.
 * Matches the header layout used across the Staff Portal entity list pages
 * (issue #878).
 */
export function ListPageHeader({ title, subtitle, actions, sx }: ListPageHeaderProps) {
  return (
    <Box
      sx={[
        {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: subtitle ? 'flex-start' : 'center',
          gap: 2,
          mb: 3,
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="h4" fontWeight={600}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
      {actions && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      )}
    </Box>
  );
}
